// Pure functions for the Ledger Loader screen: turn an exceljs worksheet into the
// CSV dialect tp_server parses, take a light look at pasted CSV, and measure the
// request body before it is sent. No Vue, no network, so it can be unit tested.
//
// CSV dialect the server parses (tp_server core.js parseCsv over csvtojson defaults):
//   comma delimiter only; header row first; LF line endings; double-quote quoting
//   with embedded quotes doubled; quote a cell only when it holds , " CR or LF;
//   empty cells emitted as nothing between commas (so a blank BL Counter is truly
//   empty, not ""); no trailing delimiter; no BOM; no trimming of data cells;
//   header cells trimmed because the server trims them anyway.

export function csvcell(v) {
  const s = v === null || v === undefined ? "" : String(v);
  return /[",\r\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
}

export function rowstocsv(headers, rows) {
  return [
    headers.map((h) => csvcell(String(h).trim())),
    ...rows.map((r) => r.map(csvcell)),
  ]
    .map((r) => r.join(","))
    .join("\n");
}

export function formatdate(d, fmt) {
  const dd = String(d.getUTCDate()).padStart(2, "0");
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const yyyy = String(d.getUTCFullYear());
  if (fmt === "YYYY-MM-DD") return yyyy + "-" + mm + "-" + dd;
  if (fmt === "MM/DD/YYYY") return mm + "/" + dd + "/" + yyyy;
  return dd + "/" + mm + "/" + yyyy;
}

// Render one exceljs cell as the text the server's validators expect:
// dates as text in the chosen layout, integers without ".0", decimals without
// exponent notation, Excel errors refused outright.
export function cellvalue(cell, dateformat) {
  let v = cell ? cell.value : null;
  if (v && typeof v === "object" && !(v instanceof Date)) {
    if (v.error) return { error: "EXCEL_ERROR", text: v.error };
    if (v.richText) v = v.richText.map((t) => t.text).join("");
    else if (v.formula !== undefined || v.sharedFormula !== undefined) v = v.result;
    else if (v.hyperlink) v = v.text;
    if (v && typeof v === "object" && !(v instanceof Date)) {
      if (v.error) return { error: "EXCEL_ERROR", text: v.error };
      if (v.richText) v = v.richText.map((t) => t.text).join("");
    }
  }
  if (v === null || v === undefined) return { text: "" };
  if (v instanceof Date) return { text: formatdate(v, dateformat) };
  if (typeof v === "number") {
    if (!isFinite(v)) return { error: "EXCEL_ERROR", text: String(v) };
    if (Number.isInteger(v)) return { text: String(v) };
    const s = v.toFixed(10).replace(/0+$/, "").replace(/\.$/, "");
    return { text: s };
  }
  if (typeof v === "boolean") return { text: v ? "true" : "false" };
  return { text: String(v) };
}

// Worksheet to CSV. Returns { csv, headers, rows } or { error, row, column, text }.
// Interior blank rows are kept so the server can flag E_INTERIOR_EMPTY_ROW;
// trailing blank rows are dropped because the server would strip them anyway.
export function sheettocsv(ws, dateformat) {
  const header = ws.getRow(1);
  let width = 0;
  header.eachCell({ includeEmpty: false }, (c, n) => {
    const cv = cellvalue(c, dateformat);
    if (!cv.error && String(cv.text).trim() !== "") width = n;
  });
  if (width === 0) return { error: "NO_HEADER" };
  const headers = [];
  for (let n = 1; n <= width; n++) {
    const cv = cellvalue(header.getCell(n), dateformat);
    headers.push(cv.error ? "" : String(cv.text).trim());
  }
  if (headers.some((h) => h === "")) return { error: "BLANK_HEADER" };

  const rows = [];
  for (let r = 2; r <= ws.rowCount; r++) {
    const row = ws.getRow(r);
    const cells = [];
    for (let n = 1; n <= width; n++) {
      const cv = cellvalue(row.getCell(n), dateformat);
      if (cv.error) {
        return { error: cv.error, row: r, column: headers[n - 1], text: cv.text };
      }
      cells.push(cv.text);
    }
    for (let n = width + 1; n <= row.cellCount; n++) {
      const cv = cellvalue(row.getCell(n), dateformat);
      if (cv.error || cv.text !== "") return { error: "DATA_BEYOND_HEADER", row: r };
    }
    rows.push(cells);
  }
  while (rows.length && rows[rows.length - 1].every((c) => c === "")) rows.pop();
  if (!rows.length) return { error: "NO_ROWS" };
  return { csv: rowstocsv(headers, rows), headers, rows: rows.length };
}

// Small RFC-4180 single-line splitter. Only used to name the columns in the
// controls dropdown; the server does the real parse.
export function splitcsvline(line) {
  const out = [];
  let cur = "";
  let inq = false;
  const s = String(line || "");
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (inq) {
      if (ch === '"') {
        if (s[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inq = false;
        }
      } else {
        cur += ch;
      }
    } else if (ch === '"') {
      inq = true;
    } else if (ch === ",") {
      out.push(cur);
      cur = "";
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out;
}

// Light look at CSV text: header names and an approximate data row count.
// Undercounts when a quoted cell holds a newline, which is why the slot says
// "about N rows" for CSV sources.
export function csvinfo(textin) {
  const t = String(textin || "")
    .replace(/^\uFEFF/, "")
    .replace(/\r\n?/g, "\n");
  if (!t.trim()) return { headers: [], rows: 0, error: "NO_ROWS" };
  const lines = t.split("\n");
  while (lines.length && lines[lines.length - 1].trim() === "") lines.pop();
  const headers = splitcsvline(lines[0]).map((h) => h.trim());
  return { headers, rows: Math.max(0, lines.length - 1), error: null };
}

// Size of the exact request body apollo will send. Measuring the CSV string alone
// is wrong: JSON escaping of quotes and newlines inflates it, and the server's
// real limit returns a bare HTTP 413 with no GraphQL error.
export function payloadbytes(query, variables) {
  const body = JSON.stringify({ operationName: null, query, variables });
  return new Blob([body]).size;
}

export const AMOUNT_RE = /^-?\d+(\.\d{1,2})?$/;
export const ROWS_RE = /^\d+$/;

// Are a slot's optional controls well formed? A total line counts only when both
// its column and its amount are filled in; half-filled lines are not valid.
export function controlsvalid(s) {
  if (!s) return true;
  const er = s.expected_rows;
  if (er !== null && er !== undefined && String(er) !== "" && !ROWS_RE.test(String(er))) {
    return false;
  }
  const totals = s.totals || [];
  return totals.every((t) => {
    const col = t.column || "";
    const amt = t.expected === null || t.expected === undefined ? "" : String(t.expected).trim();
    if (!col && !amt) return true;
    return !!col && AMOUNT_RE.test(amt);
  });
}

// The tab each file slot usually comes from in Martin's workbooks. A slot picks
// its tab by name when the workbook has it, so the SL pair ('SL Item headers' and
// 'L Item lines') and the BL statement land in the right slots without the user
// choosing. Matching ignores case and surrounding spaces.
export const SLOTTABS = {
  bl_item_headers: ["BL Item headers"],
  bl_payment_details: ["L BL Payment details", "BL Payment details"],
  nl_entries_1200: ["NL entries 1200"],
  nl_entries_2100: ["NL entries 2100"],
  sl_item_headers: ["SL Item headers"],
  sl_item_lines: ["L Item lines", "SL Item lines"],
  // Dummy payments (#153): the tabs are named after the account, so these are the
  // account codes seen so far. When neither list matches, both slots default to
  // the first tab and the server reports E_DUMMY_PAIR_IDENTICAL.
  bl_dummy_headers_1: ["WBAMEX", "SVPCONFU", "SVPCONF", "BSP"],
  bl_dummy_headers_2: ["GBTTREAS"],
};

// Default tab for a slot: the first of SLOTTABS[slot] present in the workbook,
// else the first tab (the old behaviour), else null.
export function defaultsheet(slot, sheetnames) {
  const names = sheetnames || [];
  const norm = (n) => String(n).trim().toLowerCase();
  const wanted = (SLOTTABS[slot] || []).map(norm);
  for (const w of wanted) {
    const hit = names.find((n) => norm(n) === w);
    if (hit !== undefined) return hit;
  }
  return names.length ? names[0] : null;
}

// Full CSV parse into an array of rows (arrays of strings), quoted newlines
// included. Same dialect as rowstocsv; a BOM and CRLF are tolerated for pasted text.
export function parsecsv(textin) {
  const s = String(textin || "")
    .replace(/^\uFEFF/, "")
    .replace(/\r\n?/g, "\n");
  const rows = [];
  let row = [];
  let cur = "";
  let inq = false;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (inq) {
      if (ch === '"') {
        if (s[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inq = false;
        }
      } else {
        cur += ch;
      }
    } else if (ch === '"') {
      inq = true;
    } else if (ch === ",") {
      row.push(cur);
      cur = "";
    } else if (ch === "\n") {
      row.push(cur);
      rows.push(row);
      row = [];
      cur = "";
    } else {
      cur += ch;
    }
  }
  if (cur !== "" || row.length) {
    row.push(cur);
    rows.push(row);
  }
  return rows;
}

const DATEPARTS = {
  "DD/MM/YYYY": { re: /^(\d{2})\/(\d{2})\/(\d{4})$/, d: 1, m: 2, y: 3 },
  "MM/DD/YYYY": { re: /^(\d{2})\/(\d{2})\/(\d{4})$/, d: 2, m: 1, y: 3 },
  "YYYY-MM-DD": { re: /^(\d{4})-(\d{2})-(\d{2})$/, d: 3, m: 2, y: 1 },
};

// A date cell as { y, m, d } in the chosen layout, or null when it is blank or not
// a real date in that layout (the server reports those as E_DATE_FORMAT).
export function parsedate(raw, dateformat) {
  const p = DATEPARTS[dateformat] || DATEPARTS["DD/MM/YYYY"];
  const hit = p.re.exec(String(raw === null || raw === undefined ? "" : raw).trim());
  if (!hit) return null;
  const y = parseInt(hit[p.y], 10);
  const m = parseInt(hit[p.m], 10);
  const d = parseInt(hit[p.d], 10);
  const check = new Date(Date.UTC(y, m - 1, d));
  if (y < 1 || check.getUTCFullYear() !== y || check.getUTCMonth() !== m - 1 || check.getUTCDate() !== d) {
    return null;
  }
  return { y, m, d };
}

// SL Date values in a CSV that are not today. A workbook that sets SL Date with
// =TODAY() keeps the value from the day it was last saved, so an old copy loads
// with an old date. today is a Date from the browser clock (local calendar day).
// Returns [{ date, rows }] sorted by date, date written in the chosen layout;
// an empty array when every SL Date is today, blank, unreadable or the column is
// missing.
export function sldatewarnings(csv, dateformat, today, column = "SL Date") {
  const rows = parsecsv(csv);
  if (rows.length < 2 || !(today instanceof Date)) return [];
  const want = String(column).trim().toLowerCase();
  const col = rows[0].findIndex((h) => String(h).trim().toLowerCase() === want);
  if (col < 0) return [];
  const ty = today.getFullYear();
  const tm = today.getMonth() + 1;
  const td = today.getDate();
  const counts = {};
  for (let r = 1; r < rows.length; r++) {
    const p = parsedate(rows[r][col], dateformat);
    if (!p || (p.y === ty && p.m === tm && p.d === td)) continue;
    const key = String(p.y).padStart(4, "0") + "-" + String(p.m).padStart(2, "0") + "-" + String(p.d).padStart(2, "0");
    counts[key] = counts[key] || { p, rows: 0 };
    counts[key].rows += 1;
  }
  return Object.keys(counts)
    .sort()
    .map((k) => ({
      date: formatdate(new Date(Date.UTC(counts[k].p.y, counts[k].p.m - 1, counts[k].p.d)), dateformat),
      rows: counts[k].rows,
    }));
}
